import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, Grid, Box, Button,
  Avatar, Chip, TextField, IconButton, Divider, Paper,
  Collapse, Alert, CircularProgress, Badge, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  FormControlLabel, Switch
} from '@mui/material';
import {
  Edit, PhotoCamera, LocationOn, Work, Star, Verified,
  CheckCircle, Add, Delete, ExpandMore, ExpandLess,
  Save, Cancel, Settings, Visibility, School,
  Phone, Email, SmartToy, FiberManualRecord, Share, Assignment
} from '@mui/icons-material';
import VerifierAIProfileAssistant from '../components/verifier/VerifierAIProfileAssistant';
import TierUpgradeDialog from '../components/verifier/TierUpgradeDialog';
import FileUpload from '../components/FileUpload';
import verifierService from '../services/verifierService';
import UnifiedOnboardingModal from '../components/onboarding/UnifiedOnboardingModal';
import DialogCloseButton from '../components/common/DialogCloseButton';

const BRAND_GREEN = '#43B581';
const BRAND_TEAL = '#1a8a6e';
const BRAND_DARK = '#27ae60';
const PROPERTY_CHECK_PACKAGES = [
  { slug: 'basic_photo', label: 'Standard Property Check' },
  { slug: 'detailed_condition', label: 'Detailed' },
  { slug: 'turnover', label: 'Turnover' },
  { slug: 'repair_follow_up', label: 'Repair Follow-Up' },
  { slug: 'urgent_same_day', label: 'Urgent' },
];

const VerifierProfile_Enhanced = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [tierUpgradeOpen, setTierUpgradeOpen] = useState(false);

  // Sidebar collapsed state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Edit mode per section
  const [editMode, setEditMode] = useState({});

  // Dialogs
  const [changePhotoOpen, setChangePhotoOpen] = useState(false);
  const [addCertOpen, setAddCertOpen] = useState(false);
  const [newCert, setNewCert] = useState({ name: '', issuer: '', date: '' });

  // Profile data
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',    // base_city
    avatar: null,
    bio: '',
    slug: '',
    tier: 'general',
    verificationStatus: 'pending',
    acceptsPropertyChecks: true,
    propertyCheckPackageEligibility: PROPERTY_CHECK_PACKAGES.map(pkg => pkg.slug),
    propertyCheckStandardsAcknowledged: false,
    propertyCheckTurnaroundHours: '',
    yearsExperience: '',
    specializations: [],
    certifications: [],
    stats: { rating: 0, reviewCount: 0, completedVerifications: 0, completionRate: 0 }
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const data = await verifierService.getMyProfile();
      setProfileData({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        phone: data.phone_number || data.phone || '',
        location: data.base_city || data.city || '',
        avatar: data.profile_image_url || null,
        bio: data.bio || '',
        slug: data.slug || '',
        tier: data.tier || 'general',
        verificationStatus: data.verification_status || 'pending',
        acceptsPropertyChecks: data.accepts_property_checks !== false,
        propertyCheckPackageEligibility: Array.isArray(data.property_check_package_eligibility)
          ? data.property_check_package_eligibility
          : PROPERTY_CHECK_PACKAGES.map(pkg => pkg.slug),
        propertyCheckStandardsAcknowledged: !!data.property_check_standards_acknowledged_at,
        propertyCheckTurnaroundHours: data.property_check_turnaround_hours || '',
        yearsExperience: data.years_experience || '',
        specializations: Array.isArray(data.specializations) ? data.specializations : [],
        certifications: Array.isArray(data.certifications) ? data.certifications : [],
        stats: {
          rating: Number(data.average_rating || 0),
          reviewCount: data.total_reviews || 0,
          completedVerifications: data.total_verifications || 0,
          completionRate: Number(data.completion_rate || 0)
        }
      });

      // Also load completeness
      try {
        const comp = await verifierService.getMyProfileCompleteness();
        setProfileCompleteness(comp);
      } catch (_) {
        setProfileCompleteness({ completionPercentage: 0, missingItems: [] });
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const getMissingItems = () => {
    if (profileCompleteness?.missingItems?.length) {
      return profileCompleteness.missingItems.slice(0, 4);
    }
    const missing = [];
    if (!profileData.bio) missing.push('Add bio');
    if (!profileData.avatar) missing.push('Upload photo');
    if (!profileData.specializations.length) missing.push('Add specializations');
    if (!profileData.yearsExperience) missing.push('Add experience');
    return missing.slice(0, 4);
  };

  const completenessPercent = profileCompleteness?.completionPercentage ?? (() => {
    const checks = [
      !!profileData.bio,
      !!profileData.avatar,
      profileData.specializations.length > 0,
      !!profileData.yearsExperience,
      !!profileData.phone,
      !!profileData.location
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  })();

  const handleSaveSection = async (section) => {
    try {
      setSaving(true);
      let updateData = {};
      if (section === 'about') {
        updateData = { bio: profileData.bio, baseCity: profileData.location };
      } else if (section === 'skills') {
        updateData = {
          specializations: profileData.specializations,
          yearsExperience: Number(profileData.yearsExperience) || 0
        };
      } else if (section === 'propertyChecks') {
        updateData = {
          acceptsPropertyChecks: profileData.acceptsPropertyChecks,
          propertyCheckPackageEligibility: profileData.acceptsPropertyChecks
            ? profileData.propertyCheckPackageEligibility
            : [],
          propertyCheckTurnaroundHours: Number(profileData.propertyCheckTurnaroundHours) || null,
          propertyCheckStandardsAcknowledged: profileData.propertyCheckStandardsAcknowledged,
        };
      }
      await verifierService.updateMyProfile(updateData);
      await fetchProfileData();
      setSuccess(`${section === 'about' ? 'About' : section === 'skills' ? 'Skills' : 'Property Check settings'} updated successfully`);
      setTimeout(() => setSuccess(''), 3000);
      setEditMode(prev => ({ ...prev, [section]: false }));
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCertification = async () => {
    if (!newCert.name.trim()) return;
    const updated = [...profileData.certifications, { id: Date.now(), ...newCert }];
    setProfileData(prev => ({ ...prev, certifications: updated }));
    setNewCert({ name: '', issuer: '', date: '' });
    setAddCertOpen(false);
    try {
      await verifierService.updateMyProfile({ certifications: updated });
      setSuccess('Certification added');
    } catch (err) {
      setError(err.message || 'Failed to save certification');
    }
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleRemoveCertification = async (certId) => {
    const updated = profileData.certifications.filter(c => c.id !== certId);
    setProfileData(prev => ({ ...prev, certifications: updated }));
    try {
      await verifierService.updateMyProfile({ certifications: updated });
    } catch (err) {
      setError(err.message || 'Failed to remove certification');
    }
  };

  const tierLabel = profileData.tier === 'professional' ? 'Professional Verifier' : 'General Verifier';
  const tierColor = profileData.tier === 'professional' ? '#f39c12' : BRAND_GREEN;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={56} sx={{ color: BRAND_GREEN }} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Loading your profile…</Typography>
      </Container>
    );
  }

  const missingItems = getMissingItems();
  const barColor = completenessPercent >= 70 ? BRAND_GREEN : '#f39c12';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {showOnboarding && <UnifiedOnboardingModal userRole={user?.role || 'verifier'} onComplete={() => { setShowOnboarding(false); fetchProfileData(); setSuccess('Profile updated successfully!'); setTimeout(() => setSuccess(''), 5000); }} />}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── HERO CARD ── */}
      <Card sx={{ mb: 3, overflow: 'visible', boxShadow: 3 }}>
        {/* Gradient banner */}
        <Box sx={{
          height: 180,
          background: `linear-gradient(135deg, ${BRAND_TEAL} 0%, ${BRAND_GREEN} 100%)`,
          borderRadius: '4px 4px 0 0'
        }} />

        <CardContent sx={{ textAlign: 'center' }}>
          {/* Centered avatar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '-60px', mb: 2 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  onClick={() => setChangePhotoOpen(true)}
                  sx={{ bgcolor: BRAND_GREEN, color: 'white', '&:hover': { bgcolor: BRAND_DARK }, width: 32, height: 32 }}
                >
                  <PhotoCamera fontSize="small" />
                </IconButton>
              }
            >
              <Avatar
                src={profileData.avatar}
                sx={{ width: 120, height: 120, border: '4px solid white', bgcolor: BRAND_TEAL, fontSize: 48, boxShadow: 3 }}
              >
                {profileData.firstName?.[0]?.toUpperCase() || 'V'}
              </Avatar>
            </Badge>
          </Box>

          {/* Name + status */}
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {profileData.firstName} {profileData.lastName}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={<Verified sx={{ fontSize: 14 }} />}
              label={
                profileData.verificationStatus === 'verified' ? 'Verified' :
                profileData.verificationStatus === 'suspended' ? 'Suspended' :
                profileData.verificationStatus === 'under_review' ? 'Under Review' : 'Pending'
              }
              size="small"
              sx={{
                bgcolor:
                  profileData.verificationStatus === 'verified' ? BRAND_GREEN :
                  profileData.verificationStatus === 'suspended' ? '#e74c3c' :
                  profileData.verificationStatus === 'under_review' ? '#f39c12' : '#95a5a6',
                color: 'white', fontWeight: 600
              }}
            />
            <Chip
              label={tierLabel}
              size="small"
              sx={{ bgcolor: tierColor, color: 'white', fontWeight: 600 }}
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => navigate(profileData.slug ? `/v/${profileData.slug}` : `/p/verifier/${user?.id}`)}
              sx={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
            >
              View Public Profile
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Share />}
              onClick={() => {
                const url = profileData.slug
                  ? `${window.location.origin}/v/${profileData.slug}`
                  : `${window.location.origin}/p/verifier/${user?.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2500);
                });
              }}
              sx={{ borderColor: BRAND_GREEN, color: BRAND_GREEN }}
            >
              {linkCopied ? 'Copied!' : 'Share Profile'}
            </Button>
          </Box>

          {/* Info chips */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {profileData.location && (
              <Chip icon={<LocationOn />} label={profileData.location} size="small" variant="outlined" />
            )}
            {profileData.yearsExperience && (
              <Chip icon={<Work />} label={`${profileData.yearsExperience} yrs exp`} size="small" variant="outlined" />
            )}
            {profileData.phone && (
              <Chip icon={<Phone />} label={profileData.phone} size="small" variant="outlined" />
            )}
            {profileData.email && (
              <Chip icon={<Email />} label={profileData.email} size="small" variant="outlined" />
            )}
          </Box>

          {/* Stats */}
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#ffd700' }}>
                {profileData.stats.rating.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Rating</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_GREEN }}>
                {profileData.stats.completedVerifications}
              </Typography>
              <Typography variant="caption" color="text.secondary">Verifications</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_GREEN }}>
                {profileData.stats.completionRate.toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">Completion</Typography>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {profileData.stats.reviewCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">Reviews</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── COMPLETENESS BAR ── */}
      {missingItems.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>Profile Completeness</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {completenessPercent < 100 && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setShowOnboarding(true)}
                  sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_TEAL }, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                >
                  Complete Profile →
                </Button>
              )}
              <Typography variant="body2" fontWeight={700} sx={{ color: barColor }}>{completenessPercent}%</Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completenessPercent}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {missingItems.map((item, idx) => (
              <Chip key={idx} label={`+ ${item}`} size="small" variant="outlined"
                sx={{ color: barColor, borderColor: barColor, fontSize: 11 }} />
            ))}
          </Box>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* ── MAIN COLUMN ── */}
        <Grid item xs={12} md={8}>

          {/* About Me */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>About Me</Typography>
                <IconButton size="small" onClick={() => setEditMode(p => ({ ...p, about: !p.about }))}>
                  {editMode.about ? <Cancel /> : <Edit />}
                </IconButton>
              </Box>
              {editMode.about ? (
                <>
                  <TextField
                    fullWidth multiline rows={4}
                    value={profileData.bio}
                    onChange={e => setProfileData(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Describe your verification expertise and experience…"
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    label="City / Location"
                    value={profileData.location}
                    onChange={e => setProfileData(p => ({ ...p, location: e.target.value }))}
                    size="small"
                    sx={{ mb: 2, mr: 1 }}
                  />
                  <Button variant="contained" startIcon={<Save />}
                    onClick={() => handleSaveSection('about')}
                    disabled={saving}
                    sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_DARK } }}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  {profileData.bio || 'No bio yet. Click the pencil icon to add your professional bio.'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Skills & Specializations */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>Skills & Specializations</Typography>
                <IconButton size="small" onClick={() => setEditMode(p => ({ ...p, skills: !p.skills }))}>
                  {editMode.skills ? <Cancel /> : <Edit />}
                </IconButton>
              </Box>

              {editMode.skills ? (
                <>
                  <TextField
                    fullWidth
                    label="Specializations (comma-separated)"
                    placeholder="e.g., Foundation, Structural, Roofing, Electrical, Plumbing"
                    value={profileData.specializations.join(', ')}
                    onChange={e => setProfileData(p => ({
                      ...p,
                      specializations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }))}
                    multiline rows={2}
                    helperText="Separate each specialization with a comma"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Years of Experience"
                    type="number"
                    value={profileData.yearsExperience}
                    onChange={e => setProfileData(p => ({ ...p, yearsExperience: e.target.value }))}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" startIcon={<Save />}
                      onClick={() => handleSaveSection('skills')} disabled={saving}
                      sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_DARK } }}
                    >
                      Save
                    </Button>
                    <Button variant="outlined" onClick={() => setEditMode(p => ({ ...p, skills: false }))}>Cancel</Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {profileData.specializations.length > 0
                      ? profileData.specializations.map((s, i) => (
                          <Chip key={i} label={s} size="small"
                            sx={{ bgcolor: `${BRAND_GREEN}22`, color: BRAND_DARK, fontWeight: 600 }} />
                        ))
                      : <Typography variant="body2" color="text.secondary">No specializations added yet.</Typography>
                    }
                  </Box>
                  {profileData.yearsExperience && (
                    <Typography variant="body2" color="text.secondary">
                      {profileData.yearsExperience} years of experience
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Certifications ({profileData.certifications.length})
                </Typography>
                <IconButton size="small" onClick={() => setAddCertOpen(true)}>
                  <Add />
                </IconButton>
              </Box>
              {profileData.certifications.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No certifications added yet.
                </Typography>
              ) : (
                <List dense>
                  {profileData.certifications.map(cert => (
                    <ListItem key={cert.id}>
                      <ListItemIcon><CheckCircle sx={{ color: BRAND_GREEN }} /></ListItemIcon>
                      <ListItemText primary={cert.name} secondary={`${cert.issuer} · ${cert.date}`} />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => handleRemoveCertification(cert.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ── SIDEBAR ── */}
        <Grid item xs={12} md={4}>

          {/* Smart Insights (collapsed by default) */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setAiPanelOpen(p => !p)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToy sx={{ color: BRAND_GREEN }} />
                  <Typography variant="subtitle1" fontWeight={600}>AI Profile Assistant</Typography>
                </Box>
                {aiPanelOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
              {!aiPanelOpen && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Get AI suggestions to improve your profile score.
                </Typography>
              )}
              <Collapse in={aiPanelOpen}>
                <Box sx={{ mt: 2 }}>
                  <VerifierAIProfileAssistant
                    profileData={{ personalInfo: {
                      bio: profileData.bio, avatar: profileData.avatar,
                      location: profileData.location, phone: profileData.phone,
                      email: profileData.email,
                      firstName: profileData.firstName, lastName: profileData.lastName
                    }, verifierInfo: {
                      specialization: profileData.specializations.join(', '),
                      yearsExperience: profileData.yearsExperience
                    }, certifications: profileData.certifications }}
                    onProfileUpdate={() => {}}
                    onSuggestionApply={(type, value) => {
                      if (type === 'certification') {
                        setProfileData(prev => ({
                          ...prev,
                          certifications: [...prev.certifications, { id: Date.now(), name: value, issuer: 'Suggested', date: new Date().toISOString().split('T')[0] }]
                        }));
                      }
                    }}
                  />
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Settings (collapsed by default) */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setSettingsPanelOpen(p => !p)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight={600}>Settings</Typography>
                </Box>
                {settingsPanelOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
              <Collapse in={settingsPanelOpen}>
                <Box sx={{ mt: 2 }}>
                  <List dense>
                    <ListItem
                      button
                      onClick={() => setTierUpgradeOpen(true)}
                      sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: `${BRAND_GREEN}11` } }}
                    >
                      <ListItemIcon><Verified sx={{ color: BRAND_GREEN }} /></ListItemIcon>
                      <ListItemText primary="Request Tier Upgrade" secondary="Apply to become a Professional Verifier" />
                    </ListItem>
                    <Divider />
                    <ListItem alignItems="flex-start">
                      <ListItemIcon><Assignment sx={{ color: BRAND_GREEN }} /></ListItemIcon>
                      <ListItemText
                        primary="Property Check"
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={profileData.acceptsPropertyChecks}
                                  onChange={(e) =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      acceptsPropertyChecks: e.target.checked,
                                    }))
                                  }
                                />
                              }
                              label="Accept Property Check offers"
                            />
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Preferred turnaround hours"
                              value={profileData.propertyCheckTurnaroundHours}
                              onChange={(e) =>
                                setProfileData(prev => ({
                                  ...prev,
                                  propertyCheckTurnaroundHours: e.target.value,
                                }))
                              }
                              sx={{ my: 1 }}
                              disabled={!profileData.acceptsPropertyChecks}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Eligible packages
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                              {PROPERTY_CHECK_PACKAGES.map((pkg) => {
                                const selected = profileData.propertyCheckPackageEligibility.includes(pkg.slug);
                                return (
                                  <Chip
                                    key={pkg.slug}
                                    label={pkg.label}
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                    onClick={() => {
                                      if (!profileData.acceptsPropertyChecks) return;
                                      setProfileData(prev => ({
                                        ...prev,
                                        propertyCheckPackageEligibility: selected
                                          ? prev.propertyCheckPackageEligibility.filter(slug => slug !== pkg.slug)
                                          : [...prev.propertyCheckPackageEligibility, pkg.slug],
                                      }));
                                    }}
                                  />
                                );
                              })}
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={profileData.propertyCheckStandardsAcknowledged}
                                  onChange={(e) =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      propertyCheckStandardsAcknowledged: e.target.checked,
                                    }))
                                  }
                                  disabled={!profileData.acceptsPropertyChecks}
                                />
                              }
                              label="I acknowledge Eqaya Property Check photo, GPS, and report standards"
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveSection('propertyChecks')}
                          disabled={saving}
                        >
                          Save
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon><Visibility sx={{ color: 'text.secondary' }} /></ListItemIcon>
                      <ListItemText primary="Public Profile" secondary="View how clients see your profile" />
                      <Button size="small" onClick={() => navigate(profileData.slug ? `/v/${profileData.slug}` : `/p/verifier/${user?.id}`)}>View</Button>
                    </ListItem>
                  </List>
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Quick Stats card */}
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
              Performance Overview
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Avg Rating</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Star sx={{ fontSize: 14, color: '#ffd700' }} />
                <Typography variant="body2" fontWeight={700}>{profileData.stats.rating.toFixed(1)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Verifications Done</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: BRAND_GREEN }}>
                {profileData.stats.completedVerifications}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: BRAND_GREEN }}>
                {profileData.stats.completionRate.toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Reviews</Typography>
              <Typography variant="body2" fontWeight={700}>{profileData.stats.reviewCount}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── DIALOGS ── */}

      {/* Change Photo */}
      <Dialog open={changePhotoOpen} onClose={() => setChangePhotoOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setChangePhotoOpen(false)} />
        <DialogTitle>Update Profile Photo</DialogTitle>
        <DialogContent>
          <FileUpload
            onUpload={async (url) => {
              await verifierService.updateMyProfile({ profileImageUrl: url });
              setProfileData(p => ({ ...p, avatar: url }));
              setChangePhotoOpen(false);
              setSuccess('Profile photo updated');
              setTimeout(() => setSuccess(''), 3000);
            }}
            accept="image/*"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePhotoOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Tier Upgrade */}
      <TierUpgradeDialog
        open={tierUpgradeOpen}
        onClose={() => setTierUpgradeOpen(false)}
        currentTier={profileData.tier}
      />

      {/* Add Certification */}
      <Dialog open={addCertOpen} onClose={() => setAddCertOpen(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setAddCertOpen(false)} />
        <DialogTitle>Add Certification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Certification Name"
            value={newCert.name}
            onChange={e => setNewCert(p => ({ ...p, name: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth label="Issuing Organization"
            value={newCert.issuer}
            onChange={e => setNewCert(p => ({ ...p, issuer: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label="Date Obtained" type="date"
            value={newCert.date}
            onChange={e => setNewCert(p => ({ ...p, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCertOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCertification} variant="contained"
            sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_DARK } }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VerifierProfile_Enhanced;
